import { LitElement, html, css } from 'lit';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import {
  DIMENSIONS,
  GRAINS,
  OPPORTUNITY_MODES,
  STATUSES,
  TIERS,
  isDefaultFilters,
  defaultFilters,
  toggleFilter,
} from '../lib/filters.js';
import { shortDate, humanize } from '../lib/format.js';
import { barDimensions, showsFilter, weekChoices } from '../lib/expert.js';
/** Phase 39: what choosing Sampled does, said on the control rather than left to be discovered. */
const SAMPLED_TITLE =
  'Every open deal won or lost on a coin, 500 times: the views draw the median (the weighted line) and the P10-P90 band where they can';
const DIMENSION_LABEL = {
  company: 'Company',
  department: 'Department',
  service: 'Service',
  office: 'Office',
  individual: 'Individual',
  project: 'Project',
};
const TIER_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };
const STATUS_LABEL = {
  open: 'Open',
  scheduled: 'Scheduled',
  opportunity: 'Opportunity',
  closed: 'Closed / reference',
};

/**
 * The shared filter bar (PRD 7.2). Every control writes through `setFilters`, which puts the
 * state in the URL and reads it back, so the bar is always showing what the link would
 * reproduce — and a filter set here survives a jump to another view.
 *
 * All four opportunity modes are live since Phase 39. `sampled` is the odd one: it is not a
 * filter on the allocation rows — a run writes its pipeline hours weighted and unweighted, and
 * the sampled mode is a distribution over those same hours. So the row-based views draw its
 * central line, which is the weighted stack (`pipelineReadMode`), and the band itself is read
 * from `GET /forecast/samples` by the views that can draw one. The button's title says so,
 * because a control that changes less than a reader expects should say what it changed.
 */
class FilterBar extends StoreMixin(LitElement) {
  static styles = css`
    /* Printing is this tool's image export (Phase 37): the filters are controls, and the window they chose is already on the card. */
    @media print {
      :host {
        display: none !important;
      }
    }
    :host {
      display: block;
      position: sticky;
      top: 60px;
      z-index: 20;
      background: var(--surface-2);
      border-bottom: 1px solid var(--border);
    }
    .bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 10px;
      padding: 10px var(--page-gutter);
    }
    .group {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    label.inline {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    select,
    input[type='date'],
    summary,
    button {
      font: inherit;
      font-size: 13px;
      color: var(--ink);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
      border-radius: 6px;
      padding: 5px 8px;
      cursor: pointer;
    }
    input[type='date'] {
      cursor: text;
    }

    /* Multi-selects are native <details>: open/close, Escape, and keyboard focus come free. */
    details {
      position: relative;
    }
    summary {
      list-style: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
    }
    summary::-webkit-details-marker {
      display: none;
    }
    summary::after {
      content: '▾';
      color: var(--ink-muted);
      font-size: 10px;
    }
    summary .n {
      background: var(--ink-strong);
      color: var(--surface);
      border-radius: var(--radius-pill);
      font-size: 11px;
      font-weight: 700;
      padding: 0 6px;
      line-height: 16px;
    }
    .panel {
      position: absolute;
      z-index: 40;
      top: calc(100% + 4px);
      left: 0;
      min-width: 220px;
      max-height: 320px;
      overflow: auto;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 8px;
      box-shadow: var(--shadow-menu);
      padding: 6px;
    }
    .panel .opt {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px 8px;
      border-radius: 5px;
      font-size: 13px;
      cursor: pointer;
    }
    .panel .opt:hover {
      background: var(--surface-2);
    }
    .panel .empty {
      padding: 8px;
      color: var(--ink-muted);
      font-size: 12.5px;
    }

    /* Segmented control for the opportunity mode: one row, one choice, always visible. */
    .segmented {
      display: inline-flex;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      overflow: hidden;
    }
    .segmented button {
      border: 0;
      border-radius: 0;
      border-right: 1px solid var(--control-border);
      padding: 5px 10px;
    }
    .segmented button:last-child {
      border-right: 0;
    }
    .segmented button[aria-pressed='true'] {
      background: var(--ink-strong);
      color: var(--surface);
    }
    .segmented button[disabled] {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .clear {
      margin-left: auto;
      border-color: transparent;
      background: transparent;
      color: var(--link);
      font-weight: 600;
    }
    .clear[disabled] {
      color: var(--ink-faint);
      cursor: default;
    }
  `;

  _set(patch) {
    setFilters(patch);
  }

  _toggle(key, value) {
    setFilters(toggleFilter(this.appState.filters, key, value));
  }

  _multi(key, label, options) {
    // The seven narrowing filters are an expert's (lib/expert.js). A value a link carries for a
    // hidden one is still applied — the URL is the state — and "Clear filters" still clears it.
    if (!showsFilter(key, this.appState.expert)) return '';
    const selected = this.appState.filters[key] ?? [];
    return html`
      <details>
        <summary>
          ${label}${selected.length ? html`<span class="n">${selected.length}</span>` : ''}
        </summary>
        <div class="panel">
          ${
            options.length === 0
              ? html`<div class="empty">Nothing to filter yet — the syncs have not found any.</div>`
              : options.map(
                  (opt) => html`
                    <label class="opt">
                      <input
                        type="checkbox"
                        .checked=${selected.includes(opt.value)}
                        @change=${() => this._toggle(key, opt.value)}
                      />
                      <span>${opt.label}</span>
                    </label>
                  `,
                )
          }
        </div>
      </details>
    `;
  }

  /** The entities the chosen dimension can be narrowed to. Company has none: it is the total. */
  _entityOptions() {
    const { filters, vocabularies } = this.appState;
    switch (filters.dimension) {
      case 'department':
        return vocabularies.departments;
      case 'service':
        return vocabularies.services;
      case 'office':
        return vocabularies.offices;
      case 'individual':
        return vocabularies.people.map((p) => ({ value: p.id, label: p.display_name }));
      default:
        return [];
    }
  }

  render() {
    const { filters, vocabularies, runs } = this.appState;
    const entities = this._entityOptions();

    return html`
      <div class="bar" role="group" aria-label="Filters">
        <span class="group">
          <label class="inline" for="from">From</label>
          <input
            id="from"
            type="date"
            .value=${filters.from ?? ''}
            @change=${(e) => this._set({ from: e.target.value || null })}
          />
          <select
            aria-label="Window length"
            .value=${String(filters.weeks)}
            @change=${(e) => this._set({ weeks: Number(e.target.value) })}
          >
            ${weekChoices(filters.weeks).map(
              (w) => html`<option value=${w} ?selected=${filters.weeks === w}>${w} weeks</option>`,
            )}
          </select>
          <select
            aria-label="Grain"
            .value=${filters.grain}
            @change=${(e) => this._set({ grain: e.target.value })}
          >
            ${GRAINS.map(
              (g) =>
                html`<option value=${g} ?selected=${filters.grain === g}>${humanize(g)}</option>`,
            )}
          </select>
        </span>

        <span class="group">
          <label class="inline" for="dim">By</label>
          <select
            id="dim"
            .value=${filters.dimension}
            @change=${(e) => this._set({ dimension: e.target.value, entity: null })}
          >
            ${barDimensions(DIMENSIONS, filters.dimension).map(
              (d) =>
                html`<option value=${d} ?selected=${filters.dimension === d}>
                  ${DIMENSION_LABEL[d]}
                </option>`,
            )}
          </select>
          ${
            entities.length
              ? html`
                  <select
                    aria-label="Entity"
                    .value=${filters.entity ?? ''}
                    @change=${(e) => this._set({ entity: e.target.value || null })}
                  >
                    <option value="">All</option>
                    ${entities.map(
                      (o) =>
                        html`<option value=${o.value} ?selected=${filters.entity === o.value}>
                          ${o.label}
                        </option>`,
                    )}
                  </select>
                `
              : ''
          }
        </span>

        ${this._multi('types', 'Type', vocabularies.project_types)}
        ${this._multi('services', 'Service', vocabularies.services)}
        ${this._multi('departments', 'Department', vocabularies.departments)}
        ${this._multi(
          'people',
          'People',
          vocabularies.people.map((p) => ({ value: p.id, label: p.display_name })),
        )}
        ${this._multi(
          'statuses',
          'Status',
          STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
        )}
        ${this._multi(
          'tiers',
          'Confidence',
          TIERS.map((t) => ({ value: t, label: TIER_LABEL[t] })),
        )}

        <span class="group">
          <label class="inline">Opportunities</label>
          <span class="segmented" role="group" aria-label="Opportunity mode">
            ${OPPORTUNITY_MODES.map(
              (mode) => html`
                <button
                  aria-pressed=${filters.mode === mode ? 'true' : 'false'}
                  title=${mode === 'sampled' ? SAMPLED_TITLE : ''}
                  @click=${() => this._set({ mode })}
                >
                  ${humanize(mode)}
                </button>
              `,
            )}
          </span>
        </span>

        <span class="group">
          <label class="inline" for="compare">Compare</label>
          <select
            id="compare"
            .value=${filters.compare ?? ''}
            @change=${(e) => this._set({ compare: e.target.value || null })}
          >
            <option value="">No comparison</option>
            ${(runs ?? []).map(
              (r) => html`
                <option value=${r.id} ?selected=${filters.compare === r.id}>
                  Run of ${shortDate(r.planning_date)}
                </option>
              `,
            )}
          </select>
        </span>

        <button
          class="clear"
          ?disabled=${isDefaultFilters(filters)}
          @click=${() => setFilters(defaultFilters())}
        >
          Clear filters
        </button>
      </div>
    `;
  }
}

customElements.define('filter-bar', FilterBar);
